/**
 * AITutorHome Component
 * Package upload interface and session management for AI Tutor V2
 *
 * Features:
 * - ZIP package upload (presigned URL to S3)
 * - Package list with processing status
 * - Session management (questions asked; there is no limit)
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import SchoolIcon from "@mui/icons-material/School";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import {
  fetchPackages,
  uploadPackage,
  fetchSessions,
  createSession,
} from "./aiTutorSlice";
import styles from "./AITutor.module.css";

const AITutorHome = () => {
  const { id: courseId, topicId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userProfile = useSelector((state) => state.userProfile);
  const userRole = userProfile?.data?.role;

  const {
    packages,
    packagesLoading,
    packagesError,
    sessions,
    sessionsLoading,
    uploadLoading,
    uploadError,
  } = useSelector((state) => state.aiTutor || {
    packages: [],
    packagesLoading: false,
    packagesError: null,
    sessions: [],
    sessionsLoading: false,
    uploadLoading: false,
    uploadError: null,
  });

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (topicId) {
      dispatch(fetchPackages(topicId));
      dispatch(fetchSessions());
    }
  }, [dispatch, topicId]);

  // Polling for package processing status
  const pollingStartTime = useRef(null);
  const pollingInterval = useRef(null);

  useEffect(() => {
    // Check if any packages are still processing
    const hasProcessingPackages = packages.some(
      (pkg) => pkg.processing_status === "processing" || pkg.processing_status === "pending"
    );

    if (hasProcessingPackages && topicId) {
      // Start polling if not already started
      if (!pollingStartTime.current) {
        pollingStartTime.current = Date.now();
      }

      // Check if we've exceeded 5 minutes (300000ms)
      const elapsedTime = Date.now() - pollingStartTime.current;
      if (elapsedTime >= 300000) {
        // Stop polling after 5 minutes
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
          pollingInterval.current = null;
        }
        return;
      }

      // Set up polling every 10 seconds
      if (!pollingInterval.current) {
        pollingInterval.current = setInterval(() => {
          dispatch(fetchPackages(topicId));
        }, 10000);
      }
    } else {
      // No processing packages, clear polling
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
      pollingStartTime.current = null;
    }

    // Cleanup on unmount
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [packages, topicId, dispatch]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.toLowerCase().endsWith(".zip")) {
        setUploadFile(file);
        setUploadTitle(file.name.replace(".zip", ""));
        setUploadDialogOpen(true);
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadFile(file);
      setUploadTitle(file.name.replace(".zip", ""));
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle.trim()) return;

    try {
      await dispatch(
        uploadPackage({
          topicId,
          title: uploadTitle.trim(),
          file: uploadFile,
        })
      ).unwrap();
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadTitle("");
      dispatch(fetchPackages(topicId));
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleStartLearning = async (pkg) => {
    try {
      const result = await dispatch(createSession(pkg.id)).unwrap();
      const sessionId = result.session?.session_id;
      if (sessionId) {
        navigate(`/dashboard/workspace/${courseId}/ai-tutor/learn/${sessionId}`);
      } else {
        console.error("No session_id in response:", result);
      }
    } catch (error) {
      console.error("Failed to create session:", error);
    }
  };

  const handleResumeSession = (session) => {
    navigate(`/dashboard/workspace/${courseId}/ai-tutor/learn/${session.session_id}`);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: "Pending...", color: "default" },
      processing: { label: "Processing...", color: "warning" },
      ready: { label: "Ready", color: "success" },
      failed: { label: "Failed", color: "error" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getSessionStatusChip = (status) => {
    const statusConfig = {
      active: { label: "In Progress", color: "primary" },
      completed: { label: "Completed", color: "success" },
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Filter sessions for packages in this topic
  const topicPackageIds = packages.map((p) => p.id);
  const topicSessions = sessions.filter((s) =>
    topicPackageIds.includes(s.package)
  );

  // Get package IDs that have active sessions
  const packagesWithActiveSessions = new Set(
    topicSessions
      .filter((s) => s.status === "active")
      .map((s) => s.package)
  );

  // Filter available packages - only show packages without active sessions that are ready
  const availablePackages = packages.filter(
    (pkg) => !packagesWithActiveSessions.has(pkg.id) && pkg.processing_status === "ready"
  );

  // Packages still processing
  const processingPackages = packages.filter(
    (pkg) => pkg.processing_status === "processing" || pkg.processing_status === "pending"
  );

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => navigate(`/dashboard/workspace/${courseId}`)}
            sx={{
              color: '#6b7280',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography sx={{
              fontSize: "24px",
              fontWeight: 600,
              fontFamily: 'Be Vietnam Pro',
              color: '#202124',
              display: 'flex',
              alignItems: 'center'
            }}>
              <SchoolIcon sx={{ mr: 1, color: '#0172A6' }} />
              AI Tutor
            </Typography>
            <Typography sx={{
              color: "#6b7280",
              fontSize: "14px",
              fontFamily: 'Be Vietnam Pro'
            }}>
              Interactive learning with pre-built course packages
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Upload Section - Only for Mentors */}
      {userRole === "mentor" && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              fontFamily: 'Be Vietnam Pro',
              fontWeight: 600,
              color: '#202124'
            }}
          >
            Upload Course Package
          </Typography>
          <Card
            sx={{
              borderRadius: "10px",
              border: dragActive ? "2px dashed #0172A6" : "2px dashed #E0E0E0",
              boxShadow: 'none',
              backgroundColor: dragActive ? '#e5f1f6' : '#fafafa',
              transition: 'all 0.2s ease'
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <input
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                style={{ display: "none" }}
                id="file-upload"
              />
              <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'block' }}>
                <CloudUploadIcon sx={{ fontSize: 48, color: "#0172A6", mb: 2 }} />
                <Typography sx={{
                  fontSize: "16px",
                  fontFamily: 'Be Vietnam Pro',
                  color: '#202124',
                  mb: 1
                }}>
                  Drag and drop a ZIP package here, or click to select
                </Typography>
                <Typography sx={{
                  fontSize: "14px",
                  fontFamily: 'Be Vietnam Pro',
                  color: '#6b7280'
                }}>
                  ZIP should contain session.json, chapters/, and optionally audio/ and rag/
                </Typography>
              </label>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Processing Packages */}
      {processingPackages.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              fontFamily: 'Be Vietnam Pro',
              fontWeight: 600,
              color: '#202124'
            }}
          >
            Processing Packages
          </Typography>
          {processingPackages.map((pkg) => (
            <Card
              key={pkg.id}
              sx={{
                borderRadius: "10px",
                border: "1px solid #f59e0b",
                backgroundColor: '#fffbeb',
                boxShadow: 'none',
                mb: 2
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <CircularProgress size={24} sx={{ color: '#f59e0b' }} />
                  <Box flex={1}>
                    <Typography sx={{
                      color: "#202124",
                      fontSize: "18px",
                      fontWeight: 500,
                      fontFamily: 'Be Vietnam Pro',
                    }}>
                      {pkg.title}
                    </Typography>
                    <Typography sx={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontFamily: 'Be Vietnam Pro',
                    }}>
                      {pkg.original_filename} - Processing...
                    </Typography>
                  </Box>
                  {getStatusChip(pkg.processing_status)}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Available Packages */}
      <Box className={styles.section}>
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            fontFamily: 'Be Vietnam Pro',
            fontWeight: 600,
            color: '#202124'
          }}
        >
          Available Packages
        </Typography>
        {packagesLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : packagesError ? (
          <Alert severity="error">{String(packagesError)}</Alert>
        ) : availablePackages.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: "10px",
              backgroundColor: "#e5f1f6",
              color: "#0172A6",
              '& .MuiAlert-icon': {
                color: "#0172A6"
              }
            }}
          >
            {packages.length === 0
              ? userRole === "mentor"
                ? "No packages uploaded yet. Upload a ZIP package to get started."
                : "No packages available yet. Your mentor will upload learning materials."
              : "All packages have active learning sessions. Check 'Your Learning Sessions' below."}
          </Alert>
        ) : (
          <>
            {availablePackages.map((pkg) => (
              <Card
                key={pkg.id}
                sx={{
                  borderRadius: "10px",
                  border: "1px solid #E0E0E0",
                  boxShadow: 'none',
                  mb: 3
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Package Title */}
                  <Typography sx={{
                    color: "#202124",
                    fontSize: "24px",
                    fontWeight: 500,
                    fontFamily: 'Be Vietnam Pro',
                    mb: 1
                  }}>
                    {pkg.title}
                  </Typography>

                  {/* Filename */}
                  <Typography sx={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontFamily: 'Be Vietnam Pro',
                    mb: 2
                  }}>
                    {pkg.original_filename}
                  </Typography>

                  {/* Package Info */}
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontFamily: 'Be Vietnam Pro',
                      mb: 1
                    }}>
                      Package Details
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "#F5F5F5",
                        borderRadius: "5px",
                        p: 2,
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}
                    >
                      {pkg.total_chapters > 0 && (
                        <Chip
                          icon={<MenuBookIcon />}
                          label={`${pkg.total_chapters} chapters`}
                          size="small"
                          sx={{
                            backgroundColor: '#fff',
                            border: '1px solid #E0E0E0'
                          }}
                        />
                      )}
                      {pkg.has_audio && (
                        <Chip
                          label="Audio Narration"
                          size="small"
                          sx={{
                            backgroundColor: '#e5f1f6',
                            color: '#0172A6',
                            border: 'none'
                          }}
                        />
                      )}
                      {pkg.has_rag_index && (
                        <Chip
                          icon={<QuestionAnswerIcon />}
                          label="Q&A Enabled"
                          size="small"
                          sx={{
                            backgroundColor: '#e5f1f6',
                            color: '#0172A6',
                            border: 'none'
                          }}
                        />
                      )}
                      {getStatusChip(pkg.processing_status)}
                    </Box>
                  </Box>

                  {/* Start Learning Button */}
                  <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleStartLearning(pkg)}
                    sx={{
                      textTransform: 'none',
                      fontFamily: 'Be Vietnam Pro',
                      borderRadius: '5px',
                      backgroundColor: '#0172A6',
                      px: 3,
                      py: 1,
                      '&:hover': {
                        backgroundColor: '#015d8d'
                      }
                    }}
                  >
                    Start Learning
                  </Button>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>

      {/* Sessions Section - Styled like Mentee Dashboard */}
      {topicSessions.length > 0 && (
        <Box className={styles.section}>
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              fontFamily: 'Be Vietnam Pro',
              fontWeight: 600,
              color: '#202124'
            }}
          >
            Your Learning Sessions
          </Typography>
          {topicSessions.map((session) => (
            <Card
              key={session.id}
              sx={{
                borderRadius: "10px",
                border: "1px solid #E0E0E0",
                boxShadow: 'none',
                mb: 3
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Started Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontFamily: 'Be Vietnam Pro'
                  }}>
                    Started {session.created_at ? new Date(session.created_at).toLocaleDateString() : 'Recently'}
                  </Typography>
                </Box>

                {/* Title */}
                <Typography sx={{
                  color: "#202124",
                  fontSize: "24px",
                  fontWeight: 500,
                  fontFamily: 'Be Vietnam Pro',
                  mb: 1
                }}>
                  {session.package_title}
                </Typography>

                {/* Status Badge */}
                <Box sx={{ mb: 3 }}>
                  {getSessionStatusChip(session.status)}
                </Box>

                {/* Progress Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{
                    fontSize: "14px",
                    color: "#6b7280",
                    fontFamily: 'Be Vietnam Pro',
                    mb: 1
                  }}>
                    Learning Progress
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={session.progress_percentage || 0}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: "10px",
                        backgroundColor: '#E0E0E0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#0172A6'
                        }
                      }}
                    />
                    <Typography sx={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontFamily: 'Be Vietnam Pro',
                      minWidth: '100px',
                      textAlign: 'right'
                    }}>
                      {session.progress_percentage?.toFixed(1) || 0}% complete
                    </Typography>
                  </Box>
                </Box>

                {/* Current Chapter */}
                {session.current_chapter_title && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{
                      color: "#6b7280",
                      fontSize: "14px",
                      fontFamily: 'Be Vietnam Pro',
                      mb: 1
                    }}>
                      Current Chapter
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "#F5F5F5",
                        borderRadius: "5px",
                        p: 2,
                      }}
                    >
                      <Typography sx={{
                        color: "#202124",
                        fontSize: "14px",
                        fontFamily: 'Be Vietnam Pro'
                      }}>
                        Chapter {(session.current_chunk_index || 0) + 1}: {session.current_chapter_title}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Doubts Info */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{
                    color: "#6b7280",
                    fontSize: "14px",
                    fontFamily: 'Be Vietnam Pro',
                    mb: 1
                  }}>
                    Questions Asked
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "#F5F5F5",
                      borderRadius: "5px",
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography sx={{
                      color: "#202124",
                      fontSize: "14px",
                      fontFamily: 'Be Vietnam Pro'
                    }}>
                      {session.doubt_turns || 0} asked · no limit
                    </Typography>
                  </Box>
                </Box>

                {/* Continue Learning Button */}
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleResumeSession(session)}
                  sx={{
                    textTransform: 'none',
                    fontFamily: 'Be Vietnam Pro',
                    borderRadius: '5px',
                    backgroundColor: '#0172A6',
                    px: 3,
                    py: 1,
                    '&:hover': {
                      backgroundColor: '#015d8d'
                    }
                  }}
                >
                  {session.status === "completed" ? "Review Session" : "Continue Learning"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Course Package</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Package Title"
            fullWidth
            value={uploadTitle}
            onChange={(e) => setUploadTitle(e.target.value)}
            sx={{ mt: 2 }}
          />
          {uploadFile && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              File: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {typeof uploadError === "string" ? uploadError : JSON.stringify(uploadError)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadFile || !uploadTitle.trim() || uploadLoading}
            sx={{
              backgroundColor: "#0172A6",
              "&:hover": { backgroundColor: "#015d8d" },
            }}
          >
            {uploadLoading ? <CircularProgress size={24} /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AITutorHome;
